// F30.3 — SizePills: read-only display of available sizes for the active tier
// with the recommended size highlighted. Replaces the "Recommended size: M"
// + "Add UK size →" affordances that previously lived inside the auth-only
// FIT CONFIDENCE panel (section H), now retired.
//
// Pills are NOT interactive size selectors — the active tier owns the
// recommendation and the pill row is informational only.

import type { CSSProperties } from "react";

const LABEL_MUTED = "#9B958E";
const CORAL = "#C84B2F";

const PILL_BG = "#FFFCF8";
const PILL_BORDER = "#E0D8D0";
const PILL_INK = "#4A4540";

const ACTIVE_BG = "#FAECE7";
const ACTIVE_BORDER = "#C84B2F";
const ACTIVE_INK = "#712B13";

export interface SizePillsProps {
  sizes: string[];
  recommendedSize: string | null;
  missingSize: boolean;
  onAddSize?: () => void;
}

const wrapperStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 6,
  margin: "12px 0 0",
  fontFamily: "var(--font-body)",
};

const labelStyle: CSSProperties = {
  fontSize: 11,
  color: LABEL_MUTED,
  marginRight: 2,
};

const addLinkStyle: CSSProperties = {
  fontSize: 11,
  color: CORAL,
  textDecoration: "underline",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  marginRight: 2,
  fontFamily: "inherit",
};

function pillStyle(active: boolean): CSSProperties {
  return {
    background: active ? ACTIVE_BG : PILL_BG,
    border: active ? `1.5px solid ${ACTIVE_BORDER}` : `1px solid ${PILL_BORDER}`,
    color: active ? ACTIVE_INK : PILL_INK,
    fontSize: 12,
    fontWeight: active ? 500 : 400,
    borderRadius: 20,
    padding: "4px 12px",
    lineHeight: 1.2,
    fontFamily: "inherit",
  };
}

export default function SizePills({
  sizes,
  recommendedSize,
  missingSize,
  onAddSize,
}: SizePillsProps) {
  if (!sizes || sizes.length === 0) return null;

  const normalisedRecommended = recommendedSize?.trim().toUpperCase() ?? null;

  return (
    <div
      role="group"
      aria-label={
        recommendedSize
          ? `Recommended size: ${recommendedSize}`
          : "Available sizes"
      }
      style={wrapperStyle}
    >
      {missingSize ? (
        <button type="button" onClick={onAddSize} style={addLinkStyle}>
          Add UK size →
        </button>
      ) : (
        <span style={labelStyle}>Recommended size:</span>
      )}
      {sizes.map((s) => {
        const active = normalisedRecommended === s.trim().toUpperCase();
        return (
          <span
            key={s}
            aria-current={active ? "true" : undefined}
            style={pillStyle(active)}
          >
            {s}
          </span>
        );
      })}
    </div>
  );
}
